import { Injectable, OnDestroy } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom } from 'rxjs/operators';
import * as userProfileActions from './user-profile.actions';
import * as fromAuth from '../../../authentication/store/auth';
import * as fromNotificationPrefs from 'src/app/modules/notification-prefs/store';
import { UserProfileApiService } from './user-profile-api.service';
import { OrganizationApiService } from 'src/app/modules/management/store/organizations/organization-api.service';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { DEFAULT_APP_CULTURE_CODE } from '../../languages/language.model';
import { OrgBusinessUnitsApiService } from '../../org-business-units';

@Injectable()
export class UserProfileEffects implements OnDestroy {
  constructor(
    private actions$: Actions,
    private api: UserProfileApiService,
    private store$: Store<any>,
    private translateService: TranslateService,
    private organizationApiService: OrganizationApiService,
    private orgBusinessUnitsService: OrgBusinessUnitsApiService
  ) {}

  ngOnDestroy() {}

  getUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProfileActions.getUserProfile),
      withLatestFrom(this.store$.select(fromAuth.selectUserContext)),
      switchMap(([, userContext]) => {
        return this.api.getUser(userContext.organizationUId, userContext.userProfileUId).pipe(
          switchMap(result => {
            return [
              userProfileActions.changeAppLanguage({ cultureCode: result.culture?.cultureCode }),
              userProfileActions.getUserProfileComplete({ result })
            ];
          }),
          catchError(err => {
            return of(userProfileActions.setProfileError({ err }));
          })
        );
      })
    )
  );

  changeAppLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProfileActions.changeAppLanguage),
      switchMap(action => {
        if (!!action.cultureCode) {
          // reload the page for map language changes, if needed
          let language = window.localStorage && window.localStorage.getItem('language');
          if (!!!language) {
            language = DEFAULT_APP_CULTURE_CODE;
          }
          if (language !== action.cultureCode) {
            window.localStorage.setItem('language', action.cultureCode);
            window.location.reload();
            return []; // not needed
          }
          this.translateService.use(action.cultureCode);
          moment.locale(action.cultureCode);
          return [];
        }
      })
    )
  );

  changeTranslationCulture$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProfileActions.changeTranslationCulture),
      switchMap(action => {
        this.translateService.use(action.cultureCode);
        moment.locale(action.cultureCode);
        return [];
      })
    )
  );

  useTranslationCulture$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userProfileActions.useTranslationCulture),
        switchMap(action => {
          return this.translateService.use(action.cultureCode).pipe(
            switchMap(data => {
              return [];
            })
          );
        })
      ),
    { dispatch: false }
  );

  saveUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProfileActions.saveUserProfile),
      withLatestFrom(this.store$.select(fromAuth.selectUserContext)),
      switchMap(([{ userProfile, close }, userContext]) =>
        this.api.updateUser(userContext.organizationUId, userProfile).pipe(
          switchMap(result => {
            return [userProfileActions.getUserProfileComplete({ result }), userProfileActions.showProfileForm({ showProfileForm: !close })];
          }),
          catchError(err => of(userProfileActions.setProfileError({ err })))
        )
      )
    )
  );

  updatePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProfileActions.changePassword),
      withLatestFrom(this.store$.select(fromAuth.selectUserContext)),
      switchMap(([{ oldPass, newPass }, userContext]) =>
        this.api.updatePassword(userContext.organizationUId, userContext.userProfileUId, oldPass, newPass).pipe(
          switchMap(token => {
            return [
              userProfileActions.showProfileForm({ showProfileForm: false }),
              userProfileActions.showPasswordForm({ showPasswordForm: false }),
              fromAuth.setToken({ token }),
              fromAuth.loginUserNamePasswordSuccess(),
              userProfileActions.getUserProfile(),
              userProfileActions.getUserOrganizations(),
              userProfileActions.getUserOrganizationLanguages(),
              userProfileActions.getUserOrganization()
            ];
          }),
          catchError(err => of(userProfileActions.setProfileError({ err })))
        )
      )
    )
  );

  getUserOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProfileActions.getUserOrganization),
      withLatestFrom(this.store$.select(fromAuth.selectUserContext)),
      switchMap(([action, userContext]) => {
        return this.api.getUserOrganization(userContext.organizationUId).pipe(
          map(result => {
            return userProfileActions.getUserOrganizationComplete({ userOrganization: result });
          }),
          catchError(err => {
            return of(userProfileActions.setProfileError({ err }));
          })
        );
      })
    )
  );

  getUserOrganizations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProfileActions.getUserOrganizations),
      withLatestFrom(this.store$.select(fromAuth.selectUserContext)),
      switchMap(([, userContext]) => {
        return this.api.getUserOrganizations(userContext.organizationUId, userContext.userProfileUId).pipe(
          map(result => userProfileActions.getUserOrganizationsComplete({ userOrganizations: result })),
          catchError(err => {
            return of(userProfileActions.setProfileError({ err }));
          })
        );
      })
    )
  );

  getUserBusinessUnits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProfileActions.getUserBusinessUnits),
      withLatestFrom(this.store$.select(fromAuth.selectUserContext)),
      switchMap(([, userContext]) => {
        return this.orgBusinessUnitsService.getOrgBusinessUnitsForUser(userContext.userProfileUId).pipe(
          map(result => userProfileActions.getUserBusinessUnitsComplete({ businessUnits: result })),
          catchError(err => {
            return of(userProfileActions.setProfileError({ err }));
          })
        );
      })
    )
  );

  getUserOrganizationLanguages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProfileActions.getUserOrganizationLanguages),
      withLatestFrom(this.store$.select(fromAuth.selectUserContext)),
      switchMap(([action, userContext]) => {
        return this.organizationApiService.getOrganizationLanguages(userContext.organizationUId).pipe(
          map(languages => userProfileActions.getUserOrganizationLanguagesComplete({ languages })),
          catchError(err => of(userProfileActions.setProfileError({ err })))
        );
      })
    )
  );

  showNotificationPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProfileActions.showNotificationPreferences),
      withLatestFrom(this.store$.select(fromAuth.selectToken)),
      switchMap(([, authToken]) => {
        return [
          fromNotificationPrefs.processLoginAuthCodeForEmailComplete({ authToken }),
          userProfileActions.showNotificationPreferencesComplete()
        ];
      })
    )
  );

  showNotificationPreferencesComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userProfileActions.showNotificationPreferences),
        withLatestFrom(this.store$.select(fromAuth.selectUserContext)),
        map(([, userContext]) => {
          window.open(`/update-preferences`);
        })
      ),
    { dispatch: false }
  );
}
